import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { variableNameKey } from '../../../../utils/variableScope';
import { rowKey, sortRows } from '../utils/tableUtils';

export default function useEditableOrder({
  docRows,
  campaignRows,
  scopeOptions,
  hasDocScope,
  hasCampaignScope,
  docRowsRef,
  campaignRowsRef,
  onDocRef,
  onCampRef,
}) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [orderKeys, setOrderKeys] = useState(null);

  const flatForSort = useMemo(
    () => [
      ...docRows.map((v) => ({ ...v, scope: 'doc' })),
      ...campaignRows.map((v) => ({ ...v, scope: 'campaign' })),
    ],
    [docRows, campaignRows]
  );

  const membershipKey = useMemo(
    () => flatForSort.map((r) => rowKey(r.scope, r.id)).join('|'),
    [flatForSort]
  );

  const byKey = useMemo(() => {
    const map = new Map();
    for (const v of docRows) map.set(rowKey('doc', v.id), { variable: v, scope: 'doc' });
    for (const v of campaignRows) {
      map.set(rowKey('campaign', v.id), { variable: v, scope: 'campaign' });
    }
    return map;
  }, [docRows, campaignRows]);

  useEffect(() => {
    if (sortColumn) {
      setOrderKeys(
        sortRows(flatForSort, sortColumn, sortDirection, scopeOptions).map((r) =>
          rowKey(r.scope, r.id)
        )
      );
      return;
    }
    setOrderKeys((prev) => {
      const allKeys = flatForSort.map((r) => rowKey(r.scope, r.id));
      if (!prev) return allKeys;
      const seen = new Set();
      const next = [];
      for (const k of prev) {
        if (allKeys.includes(k) && !seen.has(k)) {
          next.push(k);
          seen.add(k);
        }
      }
      for (const k of allKeys) {
        if (!seen.has(k)) next.push(k);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn, sortDirection, membershipKey, scopeOptions]);

  const editableEntries = useMemo(() => {
    const keys =
      orderKeys || flatForSort.map((r) => rowKey(r.scope, r.id));
    const entries = [];
    const seen = new Set();
    for (const key of keys) {
      const entry = byKey.get(key);
      if (entry) {
        entries.push(entry);
        seen.add(key);
      }
    }
    for (const [key, entry] of byKey) {
      if (!seen.has(key)) entries.push(entry);
    }
    return entries;
  }, [byKey, orderKeys, flatForSort]);

  const sortableIds = useMemo(
    () => editableEntries.map(({ variable, scope }) => rowKey(scope, variable.id)),
    [editableEntries]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const keys = orderKeys || sortableIds;
      const oldIndex = keys.indexOf(active.id);
      const newIndex = keys.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const nextKeys = arrayMove(keys, oldIndex, newIndex);
      setOrderKeys(nextKeys);
      setSortColumn(null);

      const nextDocs = [];
      const nextCamps = [];
      for (const key of nextKeys) {
        const entry = byKey.get(key);
        if (!entry) continue;
        if (entry.scope === 'doc') nextDocs.push(entry.variable);
        else if (entry.scope === 'campaign') nextCamps.push(entry.variable);
      }
      if (onDocRef.current && hasDocScope) {
        docRowsRef.current = nextDocs;
        onDocRef.current(nextDocs);
      }
      if (onCampRef.current && hasCampaignScope) {
        const docKeys = new Set(
          (docRows || []).map((v) => variableNameKey(v.name)).filter(Boolean)
        );
        const overlays = (campaignRowsRef.current || []).filter((v) =>
          docKeys.has(variableNameKey(v.name))
        );
        const visibleIds = new Set(nextCamps.map((v) => v.id));
        const merged = [
          ...nextCamps,
          ...overlays.filter((o) => !visibleIds.has(o.id)),
        ];
        campaignRowsRef.current = merged;
        onCampRef.current(merged);
      }
    },
    [
      orderKeys,
      sortableIds,
      byKey,
      hasDocScope,
      hasCampaignScope,
      docRows,
      docRowsRef,
      campaignRowsRef,
      onDocRef,
      onCampRef,
    ]
  );

  return {
    sortColumn,
    sortDirection,
    editableEntries,
    sortableIds,
    sensors,
    handleSort,
    handleDragEnd,
  };
}
